'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useBlockModalGpsDistanceStore } from '@/stores/useBlockModalGpsDistanceStore';
import type { GpsDistanceData } from '@/stores/useBlockModalGpsDistanceStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { MAPBOX_ACCESS_TOKEN } from '@/constants/constants';
import ImageAdaptive from '@/components/ImageAdaptive';
import Loading from '@/components/Loading';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const JAPANESE_LABEL_LAYERS = ['road-motorway-trunk', 'road-primary', 'road-number-shield'];

export default function GpsDistance() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { fieldRef } = useBlockModalGpsDistanceStore();

    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const marker = useRef<mapboxgl.Marker | null>(null);

    const initial = fieldRef?.getDistance() ?? { lat: 35.6762, lng: 139.6503 };
    const [distance, setDistance] = useState<GpsDistanceData>(initial);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    const applyJapaneseLabelsAndHideLayers = useCallback(() => {
        const m = map.current;
        const layers = m?.getStyle().layers;
        if (!layers) return;

        layers.forEach((layer) => {
            if (layer.type === 'symbol' && layer.layout && 'text-field' in layer.layout) {
                m?.setLayoutProperty(layer.id, 'text-field', ['get', 'name_ja']);
            }
        });

        JAPANESE_LABEL_LAYERS.forEach((layerId) => {
            if (m?.getLayer(layerId)) {
                m.setLayoutProperty(layerId, 'visibility', 'none');
            }
        });
    }, []);

    useEffect(() => {
        let isDisposed = false;
        if (!mapContainer.current || map.current) return;

        const initMap = (lat: number, lng: number) => {
            if (isDisposed || !mapContainer.current) return;
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: [lng, lat],
                zoom: 14,
                antialias: true,
            });

            map.current.on('style.load', () => {
                setIsMapLoaded(true);
                requestAnimationFrame(() => map.current?.resize());
            });

            map.current.once('idle', () => {
                if (!map.current) return;

                applyJapaneseLabelsAndHideLayers();

                marker.current = new mapboxgl.Marker({ draggable: true, color: '#FF6D00' })
                    .setLngLat([lng, lat])
                    .addTo(map.current!);

                marker.current.on('dragend', () => {
                    const lngLat = marker.current!.getLngLat();
                    setDistance({ lat: lngLat.lat, lng: lngLat.lng });
                });

                map.current!.on('click', (e) => {
                    const { lat, lng } = e.lngLat;
                    marker.current?.setLngLat([lng, lat]);
                    setDistance({ lat, lng });
                });
            });
        };

        if (!('geolocation' in navigator)) {
            initMap(initial.lat, initial.lng);
        } else {
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                    if (isDisposed) return;
                    const hasExisting = fieldRef?.getDistance() != null;
                    initMap(hasExisting ? initial.lat : coords.latitude, hasExisting ? initial.lng : coords.longitude);
                    if (!hasExisting) {
                        setDistance({ lat: coords.latitude, lng: coords.longitude });
                    }
                },
                () => {
                    if (isDisposed) return;
                    initMap(initial.lat, initial.lng);
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
            );
        }

        return () => {
            isDisposed = true;
            map.current?.remove();
            map.current = null;
        };
    }, [applyJapaneseLabelsAndHideLayers]);

    const handleOk = useCallback(() => {
        fieldRef?.setDistance(distance);
        closeModal();
    }, [distance, fieldRef, closeModal]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    return (
        <div className="flex flex-col h-full bg-color-block-action-modal-bg rounded-2xl overflow-hidden">
            {/* ヘッダー */}
            <div className="flex items-center px-3 h-[50px] bg-color-block-gps-modal-header flex-shrink-0">
                <span className="text-xl mr-2">
                    <ImageAdaptive src="/images/block/gps.svg" baseWidth={40} />
                </span>
                <span className="text-color-white-base font-semibold text-sm">目的地の設定</span>
            </div>

            {/* 座標表示 */}
            <div className="flex items-center gap-4 px-4 py-2 bg-color-block-action-modal-row-bg flex-shrink-0 text-xs font-mono text-color-white-base">
                <span>緯度: {distance.lat.toFixed(6)}</span>
                <span>経度: {distance.lng.toFixed(6)}</span>
            </div>

            {/* 地図 */}
            <div className="flex-1 min-h-0 relative">
                {!isMapLoaded && <Loading />}
                <div ref={mapContainer} className="w-full h-full" />
            </div>

            {/* フッター */}
            <div className="flex justify-end items-center gap-2 px-5 py-3 border-t border-color-block-action-modal-border flex-shrink-0">
                <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-color-block-action-modal-cancel text-color-block-action-modal-text-gray rounded-lg text-sm"
                >
                    キャンセル
                </button>
                <button
                    onClick={handleOk}
                    disabled={!isMapLoaded}
                    className="px-6 py-2 bg-color-block-gps-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:bg-color-block-gps-modal-ok-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    OK
                </button>
            </div>
        </div>
    );
}
